"use client"

import { useState, useEffect, useRef, memo } from "react"
import { motion } from "framer-motion"
import {
    Bell,
    ChevronDown,
    Home,
    Users,
    Grid,
    Brain,
    HelpCircle,
    Trash2,
    FolderOpen,
    Crown,
    Search,
    Settings,
    Sun,
    Moon,
    Star,
    Clock,
    Plus,
    MoreVertical,
    ChevronRight,
    Dog,
    MessageCircle,
    Heart,
    Shield,
    Bone,
    Stethoscope,
    Sparkles,
    PawPrint,
    GraduationCap,
    ExternalLink,
    Camera,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SignedIn, SignedOut, UserButton } from "@clerk/clerk-react"
import { useUser } from "@clerk/clerk-react"
import { useAnalysis } from '../../AnalysisContext'
import { getUserPlan } from '@/utils/plans'
import { UpgradeButton } from '../../pages/UpgradeButton'
import { SidebarUpgradeButton } from '../../pages/SidebarUpgradeButton'
import { useNavigate, useLocation, Link } from "react-router-dom"
import { cn } from "@/lib/utils"
import NewAnalysisModal from "../NewAnalysisModal"
import PricingModal from '../../pages/PricingModal'

function buildAnalysisItems(analysisData, breedGuide) {
    // Base section for Mi Mascota
    const baseSection = {
        section: "Mi Mascota",
        items: [
            {
                title: "Estado General",
                icon: Sparkles,
                href: "/overview",
                description: "Resumen del análisis actual",
            },
            {
                title: "Salud",
                icon: Heart,
                href: "/health",
                description: "Estado físico general",
            },
        ],
    };

    // Add care plan and nutrition if breedGuide exists
    if (breedGuide) {
        baseSection.items.push(
            {
                title: "Plan de Cuidados",
                icon: Shield,
                href: "/care",
                description: "Cuidados específicos",
            },
            {
                title: "Nutrición",
                icon: Bone,
                href: "/nutrition",
                description: "Guía alimenticia",
            }
        );
    }

    const finalAnalysisItems = [baseSection];

    // Add guide section if breed guide exists
    if (breedGuide?.cuidadosSalud?.guiaDeRaza) {
        finalAnalysisItems.push({
            section: "Guía de Raza",
            standalone: true, // Flag to indicate this is a standalone button
            href: "/characteristics",
            icon: PawPrint,
            description: "Características del ejemplar",
        });
    }

    return finalAnalysisItems;
}

const handleVetClick = (event) => {
    event.preventDefault();
    const message = encodeURIComponent(
        "¡Hola! Soy veterinario/a profesional interesado/a en unirme al equipo de DogScan AI."
    );
    window.open(`https://wa.me/525643251394?text=${message}`, "_blank");
};

const checkPremiumStatus = (user) => {
    if (!user) return false;
    if (!user.unsafeMetadata) return false;
    return user.unsafeMetadata.isPremium === true;
};

export function Sidebar() {
    const { user, isLoaded, isSignedIn } = useUser();
    const [open, setOpen] = useState(false);
    const [showUpgradeModalVetConsult, setShowUpgradeModalVetConsult] = useState(false);
    const [expandedSections, setExpandedSections] = useState({ "Mi Mascota": true });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false);

    const { analysisData, breedGuide, clearContext } = useAnalysis();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Only compute these values when user is loaded
    const isPremium = isLoaded ? checkPremiumStatus(user) : false;
    const plan = isLoaded ? getUserPlan(user) : null;

    // Return null after all hook calls
    if (!isLoaded) return null;

    const handleConsult = async (event) => {
        event.preventDefault();

        if (!isPremium) {
            setShowUpgradeModalVetConsult(true);
            return;
        }

        const message = encodeURIComponent(
            "Hola, me gustaría agendar una consulta veterinaria."
        );
        window.open(`https://wa.me/525643251394?text=${message}`, '_blank');
    };

    // Modified navigation handling to prevent blinks
    const handleNavigation = (path) => {
        // Only navigate if we're not already on this path
        if (location.pathname !== path) {
            // For mobile: close the sheet first
            setOpen(false);
            // Use navigate without any delay for smoother transitions
            navigate(path);
        }
    };

    // Desktop navigation doesn't need any special handling
    const handleNavigationDesktop = (path) => {
        // Only navigate if we're not already on this path
        if (location.pathname !== path) {
            navigate(path);
        }
    };

    return (
        <>
            {/* Desktop Sidebar */}
            <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 280, opacity: 1 }}
                className="hidden fixed left-0 top-0 bottom-0 border-r bg-gradient-to-b from-amber-50/80 via-amber-50/50 to-amber-50/30 flex-col md:flex"
            >
                <div className="bg-[#F9C74D] p-4 border-b border-amber-200/20 relative overflow-hidden">
                    {/* Subtle background patterns */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_-20%,rgba(255,255,255,0.15),transparent_70%)]"></div>
                    <div className="absolute bottom-0 right-0 w-32 h-32 opacity-[0.03]">
                        <PawPrint className="w-full h-full text-amber-900 transform rotate-12" />
                    </div>

                    <div className="flex items-center justify-between mb-4 relative">
                        <div className="flex items-center gap-3">
                            <motion.div 
                                className="relative group"
                                whileHover={{ rotate: 5, scale: 1.02 }}
                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                                {/* Enhanced glow effect */}
                                <div className="absolute inset-0 bg-gradient-to-br from-amber-400/30 to-amber-500/20 rounded-full blur-[10px] transform group-hover:scale-110 transition-transform"></div>
                                <div className="relative bg-gradient-to-br from-amber-50 to-amber-100/80 p-2 rounded-xl shadow-lg backdrop-blur-sm">
                                    <Dog className="h-7 w-7 text-amber-900 filter drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] relative z-10" />
                                </div>
                                {/* Refined status indicator */}
                                <div className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center">
                                    <div className="absolute inset-0 bg-green-400 rounded-full blur-[2px] animate-pulse"></div>
                                    <div className="h-2 w-2 bg-green-400 rounded-full border-[1.5px] border-white shadow-sm relative z-10"></div>
                                </div>
                            </motion.div>
                            <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                    <div className="relative">
                                        <h1 className="text-xl font-bold text-amber-900 tracking-tight">DogScan AI</h1>
                                        {/* Modern gradient underline */}
                                        <div className="absolute -bottom-0.5 left-0 right-0 h-[2px] bg-gradient-to-r from-amber-400/0 via-amber-400/50 to-amber-400/0"></div>
                                    </div>
                                </div>
                                {/* Enhanced status badges */}
                                <div className="flex items-center gap-2 mt-1.5">
                                    <div className="flex items-center gap-2">
                                        <div className="relative flex items-center px-2 py-1 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 rounded-full border border-emerald-500/10 shadow-sm">
                                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/50 to-transparent rounded-full backdrop-blur-sm"></div>
                                            <div className="relative flex items-center gap-1.5">
                                                <div className="relative flex">
                                                    <div className="absolute inset-0 bg-emerald-400 rounded-full blur-[1px] animate-pulse"></div>
                                                    <div className="h-1.5 w-1.5 bg-emerald-400 rounded-full relative z-10"></div>
                                                </div>
                                                <span className="text-[11px] font-medium text-emerald-800">Análisis AI</span>
                                            </div>
                                        </div>
                                        <div className="h-4 w-px bg-amber-900/10"></div>
                                        <div className="px-2 py-1 bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-full border border-amber-500/10 shadow-sm">
                                            <div className="flex items-center gap-1.5">
                                                <Clock className="h-3 w-3 text-amber-700" />
                                                <span className="text-[11px] font-medium text-amber-800">24/7</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <SignedIn>
                            <Button
                                onClick={() => {
                                    setIsModalOpen(true);
                                }}
                                className="w-full relative overflow-hidden group bg-gradient-to-r from-[#F59E0B] to-[#EA580C] hover:from-[#EA580C] hover:to-[#F59E0B] text-white shadow-sm hover:shadow-md transition-all duration-300 h-[52px] rounded-lg border border-amber-500/10 new-analysis-modal-trigger"
                            >
                                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_70%)]"></div>
                                <div className="absolute inset-0 flex items-center px-4">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-amber-500/30 p-1.5 rounded-md shadow-sm backdrop-blur-sm">
                                            <Plus className="h-4.5 w-4.5 drop-shadow-sm" />
                                        </div>
                                        <span className="text-base font-medium tracking-tight">Nuevo</span>
                                    </div>
                                    
                                    <div className="ml-auto flex items-center">
                                        <div className="flex items-center gap-1.5 bg-amber-500/30 px-2 py-1 rounded-full shadow-sm backdrop-blur-sm">
                                            <Camera className="h-3 w-3 text-amber-50" />
                                            <span className="text-xs font-medium">Análisis</span>
                                        </div>
                                    </div>
                                </div>
                            </Button>
                            <NewAnalysisModal
                                isOpen={isModalOpen}
                                onOpenChange={setIsModalOpen}
                                onSelect={() => navigate("/dog-analyzer")}
                                clearContext={clearContext}
                            />

                        </SignedIn>

                        <SignedOut>
                            <Button
                                className="w-full bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-900 hover:to-amber-800 text-amber-50 shadow-sm hover:shadow-md transition-all duration-200 rounded-lg border border-amber-700/20"
                                onClick={() => navigate("/sign-in")}
                            >
                                Iniciar Sesión
                            </Button>
                        </SignedOut>
                    </div>
                </div>

                <div className="py-2 px-4 border-b bg-gradient-to-r from-amber-50/50 to-amber-100/30">
                    <UpgradeButton plan={plan} className="w-full" />
                </div>
                <ScrollArea className="flex-1">
                    <div className="p-4 space-y-4">
                        {/* Professional Services Section - More Compact Design */}
                        <div className="mb-4 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-lg border border-emerald-200/50 overflow-hidden">
                            <div className="flex items-center justify-between px-3 py-2.5">
                                <div className="flex items-center gap-2">
                                    <Stethoscope className="h-4 w-4 text-emerald-700" />
                                    <h3 className="text-sm font-medium text-emerald-800">Asistencia Veterinaria</h3>
                                </div>
                                <Button
                                    onClick={handleConsult}
                                    className="relative overflow-hidden bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all duration-200 h-8 px-3"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium">Consultar</span>
                                        <div className="h-1.5 w-1.5 bg-emerald-300 rounded-full animate-pulse"></div>
                                    </div>
                                </Button>
                            </div>
                            <div className="px-3 pb-2.5 flex items-center gap-2">
                                <div className="flex items-center gap-1.5 text-xs text-emerald-700/70">
                                    <Shield className="h-3 w-3" />
                                    <span>Veterinarios certificados</span>
                                </div>
                                <div className="h-3 w-px bg-emerald-300/30"></div>
                                <div className="flex items-center gap-1.5 text-xs text-emerald-700/70">
                                    <Clock className="h-3 w-3" />
                                    <span>24/7</span>
                                </div>
                            </div>
                        </div>

                        <SidebarContent
                            key={`sidebar-content-${location.pathname}`}
                            analysisData={analysisData}
                            breedGuide={breedGuide}
                            expandedSections={expandedSections}
                            setExpandedSections={setExpandedSections}
                            onSelect={(path) => handleNavigationDesktop(path)}
                        />
                    </div>
                </ScrollArea>

                <div className="py-2 px-4 border-t">
                    <div
                        onClick={() => {
                            document.querySelector('.cl-userButtonTrigger')?.click();
                        }}
                        className="w-full flex items-center justify-between p-1.5 rounded-lg hover:bg-amber-50/80 transition-colors group cursor-pointer"
                    >
                        <div className="flex items-center">
                            <SignedIn>
                                <UserButton
                                    appearance={{
                                        elements: {
                                            avatarBox: "h-8 w-8",
                                            userButtonTrigger: "focus:shadow-none focus:ring-0",
                                            userButtonPopover: "bg-white shadow-lg rounded-lg border border-amber-100"
                                        }
                                    }}
                                />
                            </SignedIn>
                            <div className="ml-2">
                                <p className="text-sm font-medium">{user?.fullName || "Usuario"}</p>
                                <p className="text-xs text-muted-foreground">{user?.primaryEmailAddress?.emailAddress || ""}</p>
                            </div>
                        </div>
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                // Add settings handler here
                            }}
                        >
                            <Button
                                variant="ghost"
                                size="icon"
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <Settings className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Vet Section */}
                <div className="p-4 border-t bg-amber-100/80">
                    <div
                        onClick={handleVetClick}
                        className="w-full flex items-center px-3 py-2.5 rounded-lg 
               border border-amber-300/60 
               bg-white/60
               group overflow-hidden 
               transition-all duration-200 
               hover:border-amber-400/70 
               hover:bg-white/80
               shadow-sm
               cursor-pointer"
                    >
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2.5">
                                <div className="p-1.5 rounded-md bg-amber-200/90 group-hover:bg-amber-300/90 transition-colors">
                                    <GraduationCap className="w-3.5 h-3.5 text-amber-700" />
                                </div>
                                <span className="text-xs font-medium text-amber-900">
                                    Postular como Veterinario
                                </span>
                            </div>
                            <ExternalLink className="w-3.5 h-3.5 text-amber-500 group-hover:text-amber-600 transition-colors" />
                        </div>
                    </div>
                </div>
            </motion.div>
            <PricingModal
            isOpen={showUpgradeModalVetConsult}
            onClose={() => setShowUpgradeModalVetConsult(false)}
            plan={plan} 
          />
            {/* Mobile Sidebar */}
            <Sheet open={open} onOpenChange={setOpen}>
                {/* Keep the hamburger trigger as is */}
                <SheetTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="fixed top-3 left-3 z-50 md:hidden flex flex-col justify-center items-center gap-1.5 p-1.5 
            bg-white/90 backdrop-blur-sm hover:bg-amber-100/90 
            transition-all duration-200 
            shadow-md hover:shadow-lg
            rounded-lg
            w-10 h-10 sidebar-trigger"
                    >
                        <div className={cn(
                            "w-5 h-0.5 bg-amber-900 rounded-full absolute transition-all duration-300",
                            open ? "rotate-45 translate-y-0" : "translate-y-[-4px]"
                        )} />
                        <div className={cn(
                            "w-4 h-0.5 bg-amber-900 rounded-full absolute transition-all duration-300",
                            open ? "opacity-0" : "opacity-100"
                        )} />
                        <div className={cn(
                            "w-5 h-0.5 bg-amber-900 rounded-full absolute transition-all duration-300",
                            open ? "-rotate-45 translate-y-0" : "translate-y-[4px]"
                        )} />
                    </Button>
                </SheetTrigger>
                <SheetContent
                    side="left"
                    className={cn(
                        "w-[300px] p-0 border-r-amber-100",
                        "transition-transform duration-300 ease-in-out", 
                        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
                        isNavigating ? "!duration-0 !transition-none" : "" 
                    )}
                >
                    <SheetHeader className="bg-[#F9C74D] border-b p-4 relative overflow-hidden">
                        {/* Enhanced background patterns */}
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_-20%,rgba(255,255,255,0.2),transparent_70%)]"></div>
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.1),transparent_50%)]"></div>
                        <div className="absolute -bottom-8 -right-8 opacity-[0.03]">
                            <PawPrint className="h-32 w-32 text-amber-900 transform rotate-12" />
                        </div>
                        
                        <SheetTitle className="p-0 m-0">
                            <div className="flex flex-col gap-4">
                                <div className="flex items-center gap-3" onClick={() => handleNavigation('/')}>
                                    <motion.div 
                                        className="relative group"
                                        whileHover={{ rotate: 5, scale: 1.02 }}
                                        transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                    >
                                        {/* Enhanced glow effect */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-amber-400/30 to-amber-500/20 rounded-full blur-[10px] transform group-hover:scale-110 transition-transform"></div>
                                        <div className="relative bg-gradient-to-br from-amber-50 to-amber-100/80 p-2.5 rounded-xl shadow-lg backdrop-blur-sm">
                                            <Dog className="h-8 w-8 text-amber-900 filter drop-shadow-[0_2px_2px_rgba(0,0,0,0.1)] relative z-10" />
                                        </div>
                                        {/* Refined status indicator */}
                                        <div className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center">
                                            <div className="absolute inset-0 bg-green-400 rounded-full blur-[2px] animate-pulse"></div>
                                            <div className="h-2.5 w-2.5 bg-green-400 rounded-full border-2 border-white shadow-sm relative z-10"></div>
                                        </div>
                                    </motion.div>
                                    <div className="flex flex-col">
                                        <div className="relative">
                                            <h1 className="text-xl font-bold text-amber-900 tracking-tight">DogScan AI</h1>
                                            {/* Modern gradient underline */}
                                            <div className="absolute -bottom-0.5 left-0 right-0 h-[2px] bg-gradient-to-r from-amber-400/0 via-amber-400/50 to-amber-400/0"></div>
                                        </div>
                                        {/* Enhanced status badges */}
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <div className="flex items-center gap-2">
                                                <div className="relative flex items-center px-2 py-1 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 rounded-full border border-emerald-500/10 shadow-sm">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/50 to-transparent rounded-full backdrop-blur-sm"></div>
                                                    <div className="relative flex items-center gap-1.5">
                                                        <div className="relative flex">
                                                            <div className="absolute inset-0 bg-emerald-400 rounded-full blur-[1px] animate-pulse"></div>
                                                            <div className="h-1.5 w-1.5 bg-emerald-400 rounded-full relative z-10"></div>
                                                        </div>
                                                        <span className="text-[11px] font-medium text-emerald-800">Análisis AI</span>
                                                    </div>
                                                </div>
                                                <div className="h-4 w-px bg-amber-900/10"></div>
                                                <div className="px-2 py-1 bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-full border border-amber-500/10 shadow-sm">
                                                    <div className="flex items-center gap-1.5">
                                                        <Clock className="h-3 w-3 text-amber-700" />
                                                        <span className="text-[11px] font-medium text-amber-800">24/7</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </SheetTitle>
                    </SheetHeader>

                    <ScrollArea
                        className="h-[calc(100vh-64px)] bg-gradient-to-b from-amber-50/50 via-white to-white"
                        style={{
                            opacity: open ? 1 : 0,
                            transition: 'opacity 200ms ease-out'
                        }}
                    >
                        <div className="p-4 space-y-5">
                            <MemoizedMobileUserSection
                                user={user}
                                onSelect={(path) => handleNavigation(path)}
                                setOpen={setOpen}
                            />

                            {/* Professional Services Section - Mobile Optimized */}
                            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200/50 overflow-hidden shadow-sm">
                                <div className="flex items-center justify-between px-4 py-3">
                                    <div className="flex items-center gap-2.5">
                                        <div className="relative">
                                            <div className="absolute inset-0 bg-emerald-400/20 rounded-full blur-[6px]"></div>
                                            <Stethoscope className="h-5 w-5 text-emerald-700 relative z-10" />
                                        </div>
                                        <h3 className="text-sm font-medium text-emerald-800">Asistencia Veterinaria</h3>
                                    </div>
                                    <Button
                                        onClick={handleConsult}
                                        className="relative overflow-hidden bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all duration-200 h-8 px-3"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium">Consultar</span>
                                            <div className="h-1.5 w-1.5 bg-emerald-300 rounded-full animate-pulse"></div>
                                        </div>
                                    </Button>
                                </div>
                                <div className="px-4 pb-3 flex items-center gap-3">
                                    <div className="flex items-center gap-1.5 text-xs text-emerald-700/70">
                                        <Shield className="h-3.5 w-3.5" />
                                        <span>Veterinarios certificados</span>
                                    </div>
                                    <div className="h-3 w-px bg-emerald-300/30"></div>
                                    <div className="flex items-center gap-1.5 text-xs text-emerald-700/70">
                                        <Clock className="h-3.5 w-3.5" />
                                        <span>24/7</span>
                                    </div>
                                </div>
                            </div>

                            <MemoizedQuickActions
                                onSelect={(path) => handleNavigation(path)}
                                setOpen={setOpen}
                                setIsModalOpen={setIsModalOpen}
                                plan={plan}
                                analysisData={analysisData}
                            />
                        </div>
                    </ScrollArea>
                </SheetContent>
            </Sheet>
        </>
    )
}
function MobileUserSection({ user, onSelect, setOpen }) {
    const navigate = useNavigate();

    // Función para abrir el modal de Clerk
    const openClerkModal = () => {
        // Primero cerrar el Sheet
        setOpen(false);

        // Después de cerrar el Sheet, activar el botón de Clerk
        setTimeout(() => {
            const userButton = document.querySelector('.cl-userButtonTrigger');
            if (userButton) {
                userButton.click();
            } else {
                // Fallback en caso de que no se encuentre el botón de Clerk
                console.warn('No se encontró el botón de usuario de Clerk');
            }
        }, 300);
    };

    return (
        <div className="space-y-4">
            {/* Toda el área del div de usuario es clickeable */}
            <div
                className="flex items-center p-3 rounded-lg hover:bg-amber-50/80 transition-all duration-200 cursor-pointer"
                onClick={openClerkModal}
            >
                <Avatar className="h-10 w-10 mr-3">
                    <AvatarImage src={user?.imageUrl} />
                    <AvatarFallback>{user?.fullName?.[0] || "U"}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                    <h3 className="font-semibold text-amber-900">{user?.fullName || "Usuario"}</h3>
                    <p className="text-sm text-amber-700/70">Personal</p>
                </div>
                <ChevronRight className="h-5 w-5 text-amber-500/70" />
            </div>
        </div>
    );
}

// Memoize MobileUserSection to prevent unnecessary re-renders
const MemoizedMobileUserSection = memo(MobileUserSection);
MemoizedMobileUserSection.displayName = "MobileUserSection";

function QuickActions({ onSelect, setOpen, setIsModalOpen, plan, analysisData }) {
    // Use a handler function to prevent re-renders
    const handleButtonClick = (path) => {
        setOpen(false);
        onSelect(path);
    };

    const handleNewAnalysisClick = () => {
        setOpen(false);
        setIsModalOpen(true);
    };

    return (
        <div className="space-y-5">
            {/* Section Title with Paw Print Icon */}
            <div className="flex items-center gap-2 px-1">
                <div className="flex items-center gap-2">
                    <div className="bg-amber-100 p-1.5 rounded-full">
                        <PawPrint className="h-3.5 w-3.5 text-amber-700" />
                    </div>
                    <h3 className="text-sm font-semibold text-amber-900">Acciones Rápidas</h3>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-amber-200/0 via-amber-200 to-amber-200/0"></div>
            </div>
            
            {/* Quick Actions Grid - Enhanced with more pet-friendly styling */}
            <div className="grid grid-cols-2 gap-3">
                {/* Nuevo Análisis Button - Enhanced with paw print pattern */}
                <Button
                    className="relative overflow-hidden group bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-md hover:shadow-lg transition-all duration-200 h-[75px] rounded-xl border-0 new-analysis-modal-trigger"
                    onClick={handleNewAnalysisClick}
                >
                    {/* Background pattern with subtle paw prints */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_70%)]"></div>
                    <div className="absolute top-1 right-1 opacity-10">
                        <PawPrint className="h-12 w-12 text-white" />
                    </div>
                    
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="bg-amber-700/70 p-2.5 rounded-full mb-2 backdrop-blur-sm group-hover:scale-110 transition-transform duration-300 shadow-inner">
                            <Camera className="h-5 w-5" />
                        </div>
                        <span className="text-sm font-medium text-white">Nuevo Análisis</span>
                    </div>
                </Button>

                {/* Historial Button - Enhanced with timeline pattern */}
                <Button
                    className="relative overflow-hidden group bg-gradient-to-br from-white to-amber-50 hover:from-amber-50 hover:to-amber-100 text-amber-900 shadow-md hover:shadow-lg transition-all duration-200 h-[75px] rounded-xl border border-amber-200/40"
                    onClick={() => handleButtonClick("/historical")}
                >
                    {/* Background pattern */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.08),transparent_70%)]"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-amber-50/80 to-transparent"></div>
                    
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="bg-amber-100 p-2.5 rounded-full mb-2 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                            <FolderOpen className="h-5 w-5 text-amber-700" />
                        </div>
                        <span className="text-sm font-medium text-amber-900">Historial</span>
                    </div>
                </Button>
            </div>

            {/* Guía Rápida Button */}
            <Button
                className="w-full relative overflow-hidden group bg-gradient-to-br from-white to-amber-50 hover:from-amber-50 hover:to-amber-100 text-amber-900 shadow-sm hover:shadow-md transition-all duration-200 h-14 rounded-xl border border-amber-200/40"
                onClick={() => handleButtonClick("/faqs")}
            >
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.08),transparent_70%)]"></div>
                <div className="flex items-center gap-3 px-3">
                    <div className="bg-amber-100 p-2 rounded-full group-hover:scale-110 transition-transform duration-300">
                        <HelpCircle className="h-4 w-4 text-amber-700" />
                    </div>
                    <div className="flex flex-col items-start">
                        <span className="text-sm font-medium">Guía Rápida</span>
                        <span className="text-xs text-amber-700/70">Centro de ayuda</span>
                    </div>
                    <ChevronRight className="ml-auto h-4 w-4 text-amber-500/70" />
                </div>
            </Button>

            {/* Recent Activity Card - New component for pet owners */}
            <SidebarUpgradeButton />
        </div>
    )
}

// Memoize QuickActions to prevent unnecessary re-renders
const MemoizedQuickActions = memo(QuickActions);
MemoizedQuickActions.displayName = "QuickActions";

// Memoize SidebarContent to prevent unnecessary re-renders
const SidebarContent = memo(({ analysisData, breedGuide, expandedSections, setExpandedSections, onSelect }) => {
    const location = useLocation();
    const { user } = useUser();
    const isPremium = checkPremiumStatus(user);

    const analysisItems = analysisData ? buildAnalysisItems(analysisData, breedGuide) : [];
    const mascotaSection = analysisItems.find(section => section.section === "Mi Mascota");
    const guideSection = analysisItems.find(section => section.section === "Guía de Raza");

    const standaloneItems = [
        {
            title: "Mis Mascotas",
            icon: Clock,
            href: "/historical",
            description: "Historial de análisis previos",
            tooltipContent: "Accede a todos tus análisis anteriores"
        },
        {
            title: "Guía Rápida",
            icon: HelpCircle,
            href: "/faqs",
            description: "Centro de ayuda",
            tooltipContent: "Consulta nuestras guías y preguntas frecuentes"
        }
    ];
    
    // Use useCallback for click handlers to prevent re-renders
    const handleItemClick = (path) => {
        onSelect(path);
    };
    
    return (
        <nav className="py-3 space-y-4">
            {/* Header with paw print watermark */}
            <div className="relative mb-2">
                <div className="absolute -top-6 -right-6 opacity-5 pointer-events-none">
                    <PawPrint className="h-24 w-24 text-amber-900" />
                </div>
                
                {/* Mi Mascota Section - Clean YouTube-like style */}
                {mascotaSection && (
                    <div className="mb-4">
                        <Collapsible defaultOpen={true}>
                            <CollapsibleTrigger asChild>
                                <div className="flex items-center justify-between px-2 py-2 mb-2 cursor-pointer group">
                                    <div className="flex items-center gap-3">
                                        <div className="relative flex items-center justify-center w-8 h-8">
                                            <Heart className="h-5 w-5 text-amber-700 group-hover:scale-110 transition-transform duration-200" />
                                        </div>
                                        <span className="text-base font-medium text-amber-900">
                                            {mascotaSection.section}
                                        </span>
                                    </div>
                                    <ChevronDown className="h-5 w-5 text-amber-600 transition-transform duration-200 group-hover:text-amber-700 group-data-[state=open]:rotate-180" />
                                </div>
                            </CollapsibleTrigger>
                            
                            <CollapsibleContent>
                                <div className="space-y-1 pl-2">
                                    {mascotaSection.items.map((item) => {
                                        const isActive = location.pathname === item.href;
                                        const Icon = item.icon;

                                        return (
                                            <div 
                                                key={item.href}
                                                className={cn(
                                                    "flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer",
                                                    "transition-all duration-200",
                                                    isActive 
                                                        ? "bg-amber-100/70" 
                                                        : "hover:bg-amber-50/70"
                                                )}
                                                onClick={() => handleItemClick(item.href)}
                                            >
                                                {/* Left border indicator */}
                                                {isActive && (
                                                    <div className="absolute left-0 top-1/4 bottom-1/4 w-1 bg-amber-500 rounded-r-full" />
                                                )}
                                                
                                                <div className={cn(
                                                    "flex items-center justify-center w-8 h-8 rounded-full",
                                                    isActive 
                                                        ? "bg-amber-200/80" 
                                                        : "bg-amber-50/80"
                                                )}>
                                                    <Icon className={cn(
                                                        "h-4.5 w-4.5",
                                                        isActive 
                                                            ? "text-amber-700" 
                                                            : "text-amber-600/80"
                                                    )} />
                                                </div>
                                                
                                                <div className="flex flex-col">
                                                    <span className={cn(
                                                        "text-[15px] font-medium",
                                                        isActive ? "text-amber-900" : "text-amber-800/90"
                                                    )}>
                                                        {item.title}
                                                    </span>
                                                    <span className={cn(
                                                        "text-xs",
                                                        isActive ? "text-amber-700/90" : "text-amber-600/70"
                                                    )}>
                                                        {item.description}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                )}

                {/* Guía de Raza Button - Clean Canva-like card */}
                {guideSection && (
                    <div 
                        className={cn(
                            "flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer mb-4",
                            "transition-all duration-200",
                            location.pathname === "/characteristics" 
                                ? "bg-emerald-100/50 border-l-2 border-emerald-500" 
                                : "hover:bg-emerald-50/50 border-l-2 border-transparent"
                        )}
                        onClick={() => handleItemClick("/characteristics")}
                    >
                        <div className="relative flex items-center justify-center w-9 h-9 rounded-full bg-emerald-100/80">
                            <PawPrint className="h-5 w-5 text-emerald-700" />
                            <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                        </div>
                        
                        <div className="flex flex-col">
                            <span className="text-[15px] font-medium text-emerald-900">
                                Guía de Raza
                            </span>
                            <span className="text-xs text-emerald-700/80">
                                Características del ejemplar
                            </span>
                        </div>

                        <ChevronRight className="ml-auto h-5 w-5 text-emerald-600/70" />
                    </div>
                )}
            </div>

            {/* Section divider */}
            <div className="flex items-center gap-2 px-2 mb-3">
                <div className="h-px flex-1 bg-amber-200/30"></div>
                <span className="text-xs font-medium text-amber-700/60">Más opciones</span>
                <div className="h-px flex-1 bg-amber-200/30"></div>
            </div>

            {/* Standalone Items - Clean YouTube-like list */}
            <div className="space-y-1">
                {standaloneItems.map((item) => {
                    const isActive = location.pathname === item.href;
                    const Icon = item.icon;

                    return (
                        <div
                            key={item.href}
                            className={cn(
                                "flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer relative",
                                "transition-all duration-200",
                                isActive 
                                    ? "bg-amber-50/80 border-l-2 border-amber-500" 
                                    : "hover:bg-amber-50/40 border-l-2 border-transparent"
                            )}
                            onClick={() => handleItemClick(item.href)}
                        >
                            <div className={cn(
                                "flex items-center justify-center w-9 h-9 rounded-full",
                                isActive 
                                    ? "bg-amber-100/80" 
                                    : "bg-white/80"
                            )}>
                                <Icon className={cn(
                                    "h-5 w-5",
                                    isActive 
                                        ? "text-amber-700" 
                                        : "text-amber-600/80"
                                )} />
                            </div>
                            
                            <div className="flex flex-col">
                                <span className={cn(
                                    "text-[15px] font-medium",
                                    isActive ? "text-amber-900" : "text-amber-800"
                                )}>
                                    {item.title}
                                </span>
                                <span className={cn(
                                    "text-xs",
                                    isActive ? "text-amber-700" : "text-amber-600/70"
                                )}>
                                    {item.description}
                                </span>
                            </div>
                            
                            <ChevronRight className={cn(
                                "ml-auto h-5 w-5",
                                isActive ? "text-amber-600" : "text-amber-500/50"
                            )} />
                        </div>
                    );
                })}
            </div>
        </nav>
    );
});

// Add display name for debugging
SidebarContent.displayName = "SidebarContent";

export default Sidebar;

